/**
 * Generate a stable device fingerprint from browser characteristics.
 *
 * The hash combines several signals that are hard to spoof at the same
 * time (user agent + screen + timezone + canvas glyph rendering). It's
 * not perfect — but it's good enough to detect "I'm logging in from a
 * different physical computer" which is the threat model we care about.
 *
 * The fingerprint is stable across page reloads on the same device but
 * changes when the user moves to a different machine or browser.
 */

const _hash = (str) => {
  // Simple, stable, non-cryptographic hash (FNV-1a 32-bit) — enough as a
  // device identity marker. The backend never trusts the fingerprint as
  // proof of identity; it only uses it as a *known device key*.
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
};

const _canvasFingerprint = () => {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return 'no-canvas';
    canvas.width = 200;
    canvas.height = 50;
    ctx.textBaseline = 'top';
    ctx.font = "14px 'Arial'";
    ctx.fillStyle = '#069';
    ctx.fillText('Sanhaja-Sec-2026 ©, مرحبا', 2, 2);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fillText('Sanhaja-Sec-2026 ©, مرحبا', 4, 4);
    return _hash(canvas.toDataURL());
  } catch (e) {
    return 'canvas-err';
  }
};

export const getDeviceFingerprint = () => {
  // Prefer a cached fingerprint to keep it stable across reloads.
  const cached = localStorage.getItem('sanhaja_device_fp');
  if (cached) return cached;

  const parts = [
    navigator.userAgent || '',
    navigator.language || '',
    navigator.platform || '',
    `${screen.width}x${screen.height}x${screen.colorDepth}`,
    new Date().getTimezoneOffset().toString(),
    (navigator.hardwareConcurrency || '').toString(),
    (navigator.deviceMemory || '').toString(),
    _canvasFingerprint(),
  ].join('|');

  const fp = `FP-${_hash(parts)}-${_hash(parts.split('').reverse().join(''))}`;
  try { localStorage.setItem('sanhaja_device_fp', fp); } catch (e) {}
  return fp;
};

export const getDeviceMeta = () => {
  const ua = navigator.userAgent || '';
  let browser = 'Unknown';
  if (/edg/i.test(ua)) browser = 'Edge';
  else if (/chrome/i.test(ua)) browser = 'Chrome';
  else if (/firefox/i.test(ua)) browser = 'Firefox';
  else if (/safari/i.test(ua)) browser = 'Safari';
  let os = 'Unknown';
  if (/windows/i.test(ua)) os = 'Windows';
  else if (/macintosh|mac os/i.test(ua)) os = 'macOS';
  else if (/linux/i.test(ua)) os = 'Linux';
  else if (/android/i.test(ua)) os = 'Android';
  else if (/iphone|ipad/i.test(ua)) os = 'iOS';
  return {
    device_name: `${browser} on ${os}`,
    user_agent: ua,
    screen: `${screen.width}x${screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
};
